import React, { useCallback } from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';

import StyleConfig from 'src/constants/StyleConfig';
import Button from 'src/components/atoms/button/Button';

interface Props {
  title: string;
  color?: 'green' | 'red';
  contentText: string;
  closeText: string;
  submitText: string;
  onSubmit?: Function;
  onClose?: Function;
}

export const ConfirmModal = ({ ...props }: Props) => {
  const { title, contentText, closeText, submitText, onSubmit, onClose, color } = props;

  const wrapOnClose = useCallback(async () => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const wrapOnSubmit = useCallback(async () => {
    if (onSubmit) {
      onSubmit();
    }
  }, [onSubmit]);

  return (
    <Modal>
      <div className={color ? `is-${color}` : ''}>
        <div className="content">
          <h3 className="content-title">{title}</h3>
          <p className="content-text">{contentText}</p>
        </div>
        <div className="action">
          {onSubmit && (
            <Button
              buttonColor={color ? color : 'primary'}
              isDisabled={false}
              clickEvent={() => wrapOnSubmit()}
              text={submitText}
            />
          )}
          {onClose && (
            <Button
              buttonColor="white"
              borderColor={color}
              textColor={color}
              isDisabled={false}
              clickEvent={() => wrapOnClose()}
              text={closeText}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

ConfirmModal.defaultProps = {
  onSubmit: null,
  onClose: null,
  title: '',
  contentText: '',
  closeText: 'いいえ',
  submitText: 'はい',
};

const Modal = styled.div`
  > div {
    box-sizing: border-box;
    background: #fff;
    padding: ${StyleConfig.HELPER.M20};
    border-radius: 5px;
    width: 90%;
    margin: 0 auto;

    ${media.greaterThan('medium')`
      width: 400px;
    `};

    .content {
      .content-title {
        font-weight: bold;
        margin-bottom: 8px;
        color: ${StyleConfig.COLOR.PRIMARY};
        font-size: ${StyleConfig.TYPO.LV3};
      }
      .content-text {
        font-size: ${StyleConfig.TYPO.TEXT};
      }
    }
    .action {
      margin-top: 24px;

      > * {
        &:last-child {
          margin-top: 10px;
          border: 1px solid ${StyleConfig.COLOR.PRIMARY};
        }

        &:only-child {
          margin: 0 auto;
        }
      }
    }

    &.is-green {
      .content {
        .content-title {
          color: ${StyleConfig.COLOR.GREEN};
        }
      }
      .action {
        > * {
          &:last-child {
            border: 1px solid ${StyleConfig.COLOR.GREEN};
          }
        }
      }
    }

    &.is-red {
      .content {
        .content-title {
          color: #ff0303;
        }
      }
      .action {
        margin-top: 24px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        > * {
          width: 48%;
          margin-top: 0;
          &:first-child {
            order: 2;
          }
          &:last-child {
            order: 1;
            border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER};

            span {
              color: ${StyleConfig.COLOR.TEXT_COLOR} !important;
            }
          }
        }
      }
    }
  }
`;

export default ConfirmModal;
